import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { 
  CheckCircle2, 
  Factory, 
  Handshake, 
  Zap, 
  Target, 
  LayoutList,
  Store,
  Tag,
  Camera
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const Route = createFileRoute("/_main/quero-anunciar/")({
  component: QueroAnunciarLanding,
});

function QueroAnunciarLanding() {
  const { user, profile } = useAuth();
  const isAprovado = !!user && profile?.status === "aprovado";

  const HeroCTA = ({ onLime = false }: { onLime?: boolean }) => (
    isAprovado ? (
      <div className={cn(
        "p-8 rounded-[32px] max-w-xl mx-auto shadow-xl",
        onLime ? "bg-[#0C1A12] text-white shadow-black/20" : "bg-[#C6F764] text-[#0C1A12] shadow-[#C6F764]/20"
      )}>
        <h3 className="text-2xl font-bold mb-2">Você já é cliente Gopage</h3>
        <p className="mb-6 opacity-80 text-sm">Sua assinatura está ativa. Comece agora a divulgar seu atacado.</p>
        <Button asChild size="lg" variant={onLime ? "onDark" : "onLime"} className="font-bold px-12 h-14 text-lg rounded-full w-full sm:w-auto">
          <Link to="/minha-conta" search={{ aba: 'empresa' }}>Cadastrar minha empresa na lista</Link>
        </Button>
      </div>
    ) : (
      <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto justify-center">
        <Button asChild size="lg" variant={onLime ? "onLime" : "onLight"} className="font-semibold px-10 h-[56px] rounded-full transition-all">
          <a href="https://gopage.bio?utm_source=lista-fornecedores&utm_medium=quero-anunciar&utm_campaign=aquisicao-fornecedor">Criar meu catálogo na Gopage</a>
        </Button>
        <Button asChild size="lg" variant={onLime ? "outlineLime" : "outlineDark"} className="font-semibold px-10 h-[56px] rounded-full flex items-center justify-center transition-all">
          <Link to="/entrar">Já sou cliente Gopage — Entrar</Link>
        </Button>
      </div>
    )
  );


  return (
    <div className="bg-white min-h-screen font-poppins">
      <Helmet>
        <title>Anuncie seu atacado para lojistas de todo o Brasil | Gopage</title>
        <meta name="description" content="Divulgue seu atacado para milhares de lojistas procurando revenda. Coloque sua empresa na Lista de Fornecedores Gopage e encontre novos compradores." />
        <link rel="canonical" href="https://listafornecedores.com.br/quero-anunciar" />
      </Helmet>

      {/* Hero */}
      <section className="bg-[#0C1A12] text-white py-24 md:py-32 px-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C6F764] opacity-5 blur-[120px] rounded-full -mr-48 -mt-48"></div>
        <div className="container max-w-5xl mx-auto text-center relative z-10">
          <span className="bg-[#C6F764]/10 text-[#C6F764] border border-[#C6F764]/20 px-4 py-1.5 rounded-full font-bold tracking-wider uppercase text-[10px] mb-8 inline-block">Para fornecedores e atacadistas</span>
          <h1 className="text-4xl md:text-7xl font-bold mb-8 leading-[1.1]">Seu atacado na frente de quem compra para revender</h1>
          <p className="text-lg md:text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">Lojistas usam a Lista de Fornecedores da Gopage todos os dias para encontrar quem abastece a loja deles. Coloque sua empresa na lista.</p>
          <HeroCTA />
        </div>
      </section>

      {/* Por que anunciar */}
      <section className="py-24 px-4 bg-white">
        <div className="container max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-[#0C1A12]">Por que anunciar</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Target, title: "Audiência certa", text: "Quem entra na lista é lojista procurando fornecedor para revender. Não é curioso, é comprador." },
              { icon: Zap, title: "Contato direto", text: "O lojista fala com você no seu WhatsApp. A Gopage não entra na negociação." },
              { icon: Factory, title: "Zero comissão", text: "Você não paga percentual sobre nada que vender. Diferente de marketplace." },
              { icon: Handshake, title: "O cliente é seu", text: "O contato, o histórico e a recompra ficam com você." },
            ].map((item, i) => (
              <div key={i} className="p-8 rounded-[32px] bg-[#F7F9F5] border border-transparent hover:border-[#C6F764]/30 transition-all group">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform">
                  <item.icon className="w-6 h-6 text-[#C6F764]" />
                </div>
                <h3 className="font-bold text-xl mb-3 text-[#0C1A12]">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparativo */}
      <section className="py-24 px-4 bg-[#F7F9F5]">
        <div className="container max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-[#0C1A12]">Marketplace vs Lista Gopage</h2>
          <div className="overflow-hidden rounded-[32px] border border-gray-200 bg-white shadow-xl shadow-gray-200/50">
            <Table>
              <TableHeader className="bg-gray-50/50">
                <TableRow className="hover:bg-transparent border-b">
                  <TableHead className="py-6 px-8 text-[#0C1A12] font-bold">Funcionalidade</TableHead>
                  <TableHead className="py-6 px-8 text-[#0C1A12] font-bold bg-[#C6F764]/5">Lista Gopage</TableHead>
                  <TableHead className="py-6 px-8 text-gray-400 font-bold">Marketplace</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { f: "Comissão sobre vendas", g: "Nenhuma", m: "12% a 20%" },
                  { f: "Contato do cliente", g: "Fica com você", m: "Fica com a plataforma" },
                  { f: "Negociação", g: "Direto no WhatsApp", m: "Dentro da plataforma" },
                  { f: "Preço", g: "Você define", m: "Pressionado por comparação" },
                  { f: "Frete e prazo", g: "Sua regra", m: "Regra da plataforma" },
                ].map((row, i) => (
                  <TableRow key={i} className="hover:bg-gray-50/30 transition-colors border-b last:border-0">
                    <TableCell className="py-6 px-8 font-medium text-gray-600">{row.f}</TableCell>
                    <TableCell className="py-6 px-8 text-[#0C1A12] font-bold bg-[#C6F764]/5">{row.g}</TableCell>
                    <TableCell className="py-6 px-8 text-gray-400">{row.m}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </section>

      {/* Selo Verificado */}
      <section className="py-24 px-4">
        <div className="container max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-16 items-center bg-white p-8 md:p-16 rounded-[48px] border border-gray-100 shadow-2xl shadow-gray-200/50">
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-6 text-[#0C1A12]">O selo de Verificado é exclusivo para clientes Gopage</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">Fichas com o selo aparecem no topo da listagem e passam mais confiança para o lojista. Para receber o selo, sua empresa precisa ter uma conta Gopage ativa e o CNPJ conferido pela nossa equipe.</p>
              <div className="inline-flex items-center gap-3 bg-[#C6F764] px-6 py-3 rounded-full font-bold text-[#0C1A12] shadow-lg shadow-[#C6F764]/20">
                <CheckCircle2 className="w-5 h-5" /> Verificado
              </div>
            </div>
            <div className="w-full md:w-72 h-48 bg-[#F7F9F5] rounded-[32px] border border-dashed border-gray-200 flex items-center justify-center relative group">
              <div className="bg-white p-6 rounded-2xl shadow-xl flex items-center gap-4 border border-gray-100 group-hover:scale-105 transition-transform">
                <div className="w-12 h-12 bg-gray-100 rounded-xl"></div>
                <div>
                  <div className="w-24 h-3 bg-gray-200 rounded-full mb-2"></div>
                  <div className="flex items-center gap-1.5 bg-[#C6F764] px-2.5 py-1 rounded-full text-[10px] font-bold text-[#0C1A12]">
                    <CheckCircle2 className="w-3 h-3" /> Verificado
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bloco de Conversão */}
      <section className="py-24 px-4 bg-[#F7F9F5]">
        <div className="container max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-[#0C1A12]">Com a conta Gopage você ganha muito mais que o selo</h2>
          <p className="text-xl text-gray-600 mb-16 leading-relaxed">A Gopage é a plataforma de catálogo digital que seus clientes lojistas usam para comprar de você.</p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-20 text-left">
            {[
              { icon: LayoutList, title: "Catálogo digital com link único", text: "Seus clientes veem todos os produtos, preços e disponibilidade sem você mandar print, PDF ou áudio no WhatsApp." },
              { icon: Store, title: "Pedido organizado", text: "O lojista monta o pedido sozinho e ele chega pronto no seu WhatsApp. Acabou anotar em caderno." },
              { icon: Tag, title: "Preço por cliente", text: "Trabalhe tabela de atacado e varejo no mesmo catálogo." },
              { icon: Camera, title: "Sacolinha do Instagram", text: "O mesmo catálogo alimenta seu perfil, sem cadastrar produto duas vezes." },
            ].map((b, i) => (
              <div key={i} className="flex gap-6 p-8 bg-white rounded-3xl shadow-sm border border-gray-100">
                <div className="shrink-0 w-12 h-12 bg-[#F7F9F5] rounded-xl flex items-center justify-center">
                  <b.icon className="w-6 h-6 text-[#C6F764]" />
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2 text-[#0C1A12]">{b.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{b.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#0C1A12] p-12 md:p-16 rounded-[48px] shadow-2xl max-w-2xl mx-auto relative overflow-hidden text-white">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#C6F764] opacity-10 rounded-full -mr-16 -mt-16"></div>
            <h3 className="text-2xl font-bold mb-2">Comece hoje mesmo</h3>
            <div className="text-4xl font-black mb-6 text-[#C6F764]">A partir de R$ 39,90<span className="text-base font-medium text-gray-400">/mês</span></div>
            <Button asChild size="lg" variant="onDark" className="px-8 h-[56px] rounded-full w-full mb-4 transition-all">
              <a href="https://gopage.bio?utm_source=lista-fornecedores&utm_medium=quero-anunciar&utm_campaign=aquisicao-fornecedor">Assinar Gopage e Anunciar</a>
            </Button>
            <p className="text-xs text-gray-400 mt-4">Assinando a Gopage, você libera o cadastro na lista e o selo de Verificado.</p>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-24 px-4">
        <div className="container max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-[#0C1A12]">Como funciona</h2>
          <div className="grid md:grid-cols-3 gap-12 relative">
            <div className="absolute top-1/2 left-0 w-full h-px bg-gray-100 -translate-y-1/2 hidden md:block"></div>
            {[
              { n: 1, t: "Crie sua conta Gopage", d: "Monte seu catálogo e organize seus produtos." },
              { n: 2, t: "Cadastre-se na lista", d: "Informe seu CNPJ e dados da empresa." },
              { n: 3, t: "Receba o selo", d: "Nossa equipe confere e sua ficha entra no ar." },
            ].map((step, i) => (
              <div key={i} className="relative z-10 bg-white">
                <div className="w-16 h-16 bg-[#0C1A12] text-[#C6F764] rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-6 shadow-xl shadow-black/10">
                  {step.n}
                </div>
                <h4 className="font-bold text-lg mb-2 text-[#0C1A12]">{step.t}</h4>
                <p className="text-gray-500 text-sm px-4 leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4 bg-[#F7F9F5]">
        <div className="container max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16 text-[#0C1A12]">Dúvidas Frequentes</h2>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {[
              { q: "Quanto custa para anunciar na lista?", a: "O anúncio na lista está incluso no seu plano de cliente Gopage. Não há custo extra para aparecer." },
              { q: "Preciso ser cliente Gopage para aparecer?", a: "Sim, o destaque e o selo de Verificado são exclusivos para clientes da nossa plataforma de catálogo digital." },
              { q: "A Gopage cobra comissão sobre o que eu vender?", a: "Não. A Gopage não participa da transação. Você recebe o pedido no WhatsApp e negocia direto com o lojista." },
              { q: "Quem vai ver minha empresa?", a: "Milhares de lojistas profissionais que buscam fornecedores qualificados todos os dias na nossa plataforma." },
              { q: "Quanto tempo leva para minha ficha entrar no ar?", a: "Após o cadastro, nossa equipe faz a conferência dos dados em até 2 dias úteis." },
              { q: "Posso editar meus dados depois?", a: "Sim, você terá acesso a um painel para atualizar fotos, telefones e descrição sempre que precisar." },
              { q: "Minha empresa já está na lista, como faço para gerenciá-la?", a: "Basta entrar com seu e-mail de cliente Gopage e clicar em 'Minha Empresa' para reivindicar ou editar a ficha." },
            ].map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white px-8 rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <AccordionTrigger className="text-left font-bold text-[#0C1A12] py-6 hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-6 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Quanto custa para anunciar na lista?",
                  "acceptedAnswer": { "@type": "Answer", "text": "O anúncio na lista está incluso no seu plano de cliente Gopage." }
                },
                {
                  "@type": "Question",
                  "name": "Preciso ser cliente Gopage para aparecer?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Sim, o destaque e o selo de Verificado são exclusivos para clientes da nossa plataforma." }
                }
              ]
            })}
          </script>
        </div>
      </section>
      
      {/* CTA Final */}
      <section className="bg-[#C6F764] py-20 px-4 text-center">
        <div className="container max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black mb-4 text-[#0C1A12]">Pronto para encontrar novos lojistas?</h2>
          <p className="text-[#1A2E22] text-lg mb-10 font-medium">Coloque sua empresa na frente de quem compra para revender.</p>
          <HeroCTA onLime />
        </div>
      </section>
    </div>
  );
}
